// src/components/admin/AdminLayout.js
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminDashboard from './AdminDashboard';
import ContentModeration from './ContentModeration';
import PayoutDashboard from './PayoutDashboard';
import UserManagement from './UserManagement';
import AnalyticsTestPanel from './AnalyticsTestPanel';
import PodcastAnalyticsPage from './PodcastAnalyticsPage';
import PublicServiceManager from './PublicServiceManager';

const AdminLayout = () => {
  const { currentUser, userProfile, isAdmin, logout } = useAuth();
  const location = useLocation();

  // Show loading while profile is being fetched
  if (!currentUser) return <Navigate to="/login" replace />;
  if (!userProfile) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
      </div>
    );
  }
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const navigation = [
    { name: 'Oversigt', href: '/admin/dashboard', icon: '📊' },
    { name: 'Podcast Analytics', href: '/admin/podcast-analytics', icon: '🎙️' },
    { name: 'Indhold', href: '/admin/moderation', icon: '📻' },
    { name: 'Brugere', href: '/admin/users', icon: '👥' },
    { name: 'Udbetalinger', href: '/admin/payouts', icon: '💰' },
    { name: 'Public service', href: '/admin/public-service', icon: '📡' },
    { name: 'Analytics Test', href: '/admin/analytics-test', icon: '🧪' },
    { name: 'Indstillinger', href: '/admin/settings', icon: '⚙️' },
  ];

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <div className="w-64 bg-slate-800 border-r border-slate-700">
        <div className="p-6">
          <h1 className="text-xl font-bold text-white">Faircast Admin</h1>
          <p className="text-slate-400 text-sm mt-1">Administratorpanel</p>
        </div>
        
        <nav className="mt-6">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white border-r-2 border-blue-400'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <span className="w-5 h-5 mr-3 text-center">{item.icon}</span>
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User info & logout */}
        <div className="absolute bottom-0 w-64 p-6 border-t border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white">{userProfile.username}</p>
              <p className="text-xs text-slate-400">Administrator</p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-slate-400 hover:text-white transition-colors"
              title="Log ud"
            >
              🚪
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
          <h2 className="text-lg font-semibold text-white">
            {navigation.find(item => item.href === location.pathname)?.name || 'Admin'}
          </h2>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <Routes>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="podcast-analytics" element={<PodcastAnalyticsPage />} />
            <Route path="moderation" element={<ContentModeration />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="payouts" element={<PayoutDashboard />} />
            <Route path="public-service" element={<PublicServiceManager />} />
            <Route path="analytics-test" element={<AnalyticsTestPanel />} />
            <Route path="settings" element={<div className="text-white">Indstillinger kommer snart...</div>} />
            <Route path="" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;